import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import type { ChildCustomerOverview } from '@weft/types';
import { uniqueObjects } from '@weft/utils';
import { AutocompleteSelect } from 'apps/app/src/components/design/AutoCompleteSelect';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { PlusIcon } from 'apps/app/src/components/icons/PlusIcon';
import { usePotentialCustomerChildren } from 'apps/app/src/hooks/requests/use-potential-customer-children';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { useState } from 'react';

type AddCustomerChildModalProps = {
  disabled?: boolean;
  onChildSelect?: (childId: ChildCustomerOverview) => void;
  childrenToExclude?: ChildCustomerOverview[];
  childremToAdd?: ChildCustomerOverview[];
  customerId: string;
};

export const AddCustomerChildModal = (
  { customerId, childremToAdd, childrenToExclude, disabled, onChildSelect }: AddCustomerChildModalProps,
) => {
  const [selectedChild, setSelectedChild] = useState<ChildCustomerOverview>();
  const { handleClose, handleOpen, isDialogOpen } = useDialog();
  const { data: { potentialChildCustomers } } = usePotentialCustomerChildren();

  const selectableChildren = getSelectableChildren(customerId, potentialChildCustomers, childrenToExclude, childremToAdd);

  const handleDialogSubmit = () => { 
    selectedChild && onChildSelect?.(selectedChild);
    handleClose();
  };

  return (
    <>
      <div>
        <button
          type="button"
          className="text-icon enabled:text-style group flex items-center space-x-1 disabled:cursor-default"
          onClick={handleOpen}
          disabled={disabled || selectableChildren.length === 0}
        >
          <PlusIcon className="text-icon enabled:group-hover:text-style h-6 w-6"/>
          <label className="cursor-pointer pl-2 text-sm group-disabled:cursor-default" >
            Add related entity
          </label>
        </button>

        <DefaultThemeDialog
          open={isDialogOpen}
          onClose={handleClose}
          fullWidth
          PaperProps={{
            className: 'max-w-[550px] px-6 pt-6',
          }}
        >
          <DialogTitle>Add Related Entity</DialogTitle>
          <DialogContent className='mb-4'>
            <DialogContentText>Add related entities and subsidiaries to this parent account</DialogContentText>
            <div className="mt-5 flex">
              <AutocompleteSelect 
                options={selectableChildren}
                className='max-w-[99%] grow'
                label='Select Entity'
                getOptionLabel={(option: ChildCustomerOverview) => `${option.name} - ${option.externalId}`}
                onChange={(_, value: ChildCustomerOverview) => setSelectedChild(value)}
                textFieldProps={{
                  placeholder: 'Select Entity',
                  focused: true,
                }}
              />
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              variant="text"
              color="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <button
              type="button"
              onClick={handleDialogSubmit}
              className="inline-flex justify-center rounded-md border border-transparent
                bg-blue-100 px-4 py-2 text-base font-medium text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 enabled:hover:bg-blue-200 disabled:opacity-50"
            >
              Confirm
            </button>
          </DialogActions>
        </DefaultThemeDialog>
      </div>
    </>
  );
};

function getSelectableChildren(customerId: string, children: ChildCustomerOverview[], childrenToExclude: ChildCustomerOverview[] = [], childrenToAdd: ChildCustomerOverview[] = []) {
  const allChildren = uniqueObjects([...children, ...childrenToAdd]);
  const childrenWithoutCurrentCustomer = allChildren.filter((child) => child.id !== customerId);
  
  return childrenWithoutCurrentCustomer.filter((child) => !childrenToExclude.some((childToExclude) => childToExclude.id === child.id));
}